import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/format";
import { toast } from "sonner";

const overlay = { background: "rgba(0,0,0,0.8)" };
const panel = { background: "var(--bg-modal)", border: "1px solid var(--border)" };
const closeCls = "text-sm opacity-60 hover:opacity-100 shrink-0";

function FlagDetail({ flag, itemLabel, onClose, onUnflagged }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const unflag = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.delete(`/flags/${flag.id}`);
      toast.success("unflagged");
      onUnflagged(flag.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-6 fade-in" style={overlay} onClick={onClose} data-testid="flag-detail-overlay">
      <div className="w-full max-w-md p-8 max-h-[85vh] flex flex-col" style={panel} onClick={(e) => e.stopPropagation()} data-testid="flag-detail-modal">
        <div className="flex justify-between items-start mb-6 gap-4 shrink-0">
          <h3 className="font-head text-lg break-words" style={{ color: "var(--text-primary)" }} data-testid="flag-detail-name">{flag.target_user_name || "unknown"}</h3>
          <button onClick={onClose} data-testid="flag-detail-close" className={closeCls} style={{ color: "var(--text-primary)" }}>[ close ]</button>
        </div>
        <div className="overflow-y-auto flex-1 pr-1">
          <label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{itemLabel}</label>
          <p className="long-text-sm text-sm leading-relaxed mt-2 mb-6" style={{ color: "var(--text-primary)" }} data-testid="flag-detail-ask">{flag.ask_text || "—"}</p>
          <label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>provisions</label>
          <p className="text-sm mt-2 mb-6" style={{ color: "var(--accent)" }} data-testid="flag-detail-provisions">+{flag.provisions ?? 0}</p>
          <label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>appreciation</label>
          <p className="long-text-sm text-sm leading-relaxed mt-2" style={{ color: "var(--text-secondary)" }} data-testid="flag-detail-appreciation">{flag.appreciation || "—"}</p>
        </div>
        {user?.is_god && (
          <button onClick={unflag} disabled={busy} data-testid="flag-detail-unflag-btn" className="btn w-full py-3 mt-6 text-sm tracking-widest shrink-0">{busy ? "…" : "UNFLAG"}</button>
        )}
      </div>
    </div>
  );
}

function FlagList({ category, title, itemLabel, onClose }) {
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get(`/flags?category=${category}`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [category]);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 fade-in" style={overlay} onClick={onClose} data-testid="flag-list-overlay">
      <div className="w-full max-w-md p-8 max-h-[85vh] flex flex-col" style={panel} onClick={(e) => e.stopPropagation()} data-testid="flag-list-modal">
        <div className="flex justify-between items-start mb-6 gap-4">
          <span className="text-sm uppercase tracking-widest" style={{ color: "var(--text-primary)" }} data-testid="flag-list-title">{title}</span>
          <button onClick={onClose} data-testid="flag-list-close" className={closeCls} style={{ color: "var(--text-primary)" }}>[ close ]</button>
        </div>
        <div className="overflow-y-auto flex flex-col gap-2" data-testid="flag-list">
          {!items ? (
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="flag-list-empty">Nobody here yet.</p>
          ) : (
            items.map((f) => (
              <button key={f.id} onClick={() => setDetail(f)} data-testid={`flag-list-row-${f.id}`} className="text-left px-4 py-3 text-sm transition-colors hover:bg-[var(--bg-secondary)]" style={{ border: "1px solid var(--border)", color: "var(--text-primary)" }}>
                <span className="flex justify-between gap-4">
                  <span className="break-words">{f.target_user_name || "unknown"}</span>
                  <span style={{ color: "var(--accent)" }} data-testid={`flag-list-provisions-${f.id}`}>+{f.provisions ?? 0}</span>
                </span>
              </button>
            ))
          )}
        </div>
      </div>
      {detail && (
        <div onClick={(e) => e.stopPropagation()}>
          <FlagDetail flag={detail} itemLabel={itemLabel} onClose={() => setDetail(null)} onUnflagged={(id) => { setItems((p) => p.filter((f) => f.id !== id)); setDetail(null); }} />
        </div>
      )}
    </div>
  );
}

export function FlagListButton({ category, label, title, itemLabel, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={testId} className="btn px-6 py-2 text-xs tracking-widest">{label}</button>
      {open && createPortal(<FlagList category={category} title={title} itemLabel={itemLabel} onClose={() => setOpen(false)} />, document.body)}
    </>
  );
}

export function RemarkableMindsButton() {
  return <FlagListButton category="genius" label="REMARKABLE MINDS" title="ask a wise question" itemLabel="ask" testId="remarkable-minds-btn" />;
}

export function OnlyTheBraveButton() {
  return <FlagListButton category="brave" label="ONLY THE BRAVE" title="only the brave" itemLabel="undertaking" testId="only-the-brave-btn" />;
}
